"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { uk, enUS } from "date-fns/locale";
import {
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { useLanguage } from "@/lib/i18n/LanguageContext";

type DateFieldMode = "date" | "datetime";

interface DateFieldProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  mode?: DateFieldMode;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  allowClear?: boolean;
  minutesStep?: number;
  calendarStartDay?: number;
  label?: string;
  required?: boolean;
  name?: string;
  id?: string;
  helperText?: string;
  openToDate?: Date;
  /** Ties a parent-rendered error to the input. */
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
}

const DATE_FORMAT = "dd.MM.yyyy";
const DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm";

const DATE_INPUT_FORMATS = [
  "dd.MM.yyyy",
  "d.M.yyyy",
  "dd/MM/yyyy",
  "d/M/yyyy",
  "yyyy-MM-dd",
];

const DATE_TIME_INPUT_FORMATS = [
  "dd.MM.yyyy HH:mm",
  "dd.MM.yyyy H:mm",
  "d.M.yyyy HH:mm",
  "d.M.yyyy H:mm",
  "dd/MM/yyyy HH:mm",
  "dd/MM/yyyy H:mm",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd H:mm",
  "yyyy-MM-dd'T'HH:mm",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm:ss.SSS",
];

const formatDateDigits = (digits: string) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}${digits.length > 4 ? "." : ""}${digits.slice(4)}`;
};

const formatDateTimeDigits = (digits: string) => {
  if (digits.length <= 8) return formatDateDigits(digits);
  const datePart = formatDateDigits(digits.slice(0, 8));
  const timeDigits = digits.slice(8);
  if (timeDigits.length <= 2) return `${datePart} ${timeDigits}`;
  return `${datePart} ${timeDigits.slice(0, 2)}:${timeDigits.slice(2)}`;
};

const sanitizeUserInput = (value: string, mode: DateFieldMode) => {
  const digitsOnly = value.replace(/\D/g, "");
  const maxDigits = mode === "datetime" ? 12 : 8;
  const clipped = digitsOnly.slice(0, maxDigits);
  return mode === "datetime" ? formatDateTimeDigits(clipped) : formatDateDigits(clipped);
};

const hasCompleteInput = (value: string, mode: DateFieldMode) => {
  const digitsCount = value.replace(/\D/g, "").length;
  return mode === "datetime" ? digitsCount >= 12 : digitsCount >= 8;
};

const formatValue = (value: Date | null, mode: DateFieldMode) => {
  if (!value) return "";
  const safeValue = new Date(value);
  if (!isValid(safeValue)) return "";
  return format(safeValue, mode === "datetime" ? DATE_TIME_FORMAT : DATE_FORMAT);
};

const ensureWithinBounds = (
  date: Date | null,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  mode: DateFieldMode
): { valid: true; date: Date | null; reason?: undefined } | { valid: false; date: null; reason: 'minDate' | 'maxDate' } => {
  if (!date) return { valid: true, date: null };
  const left = mode === "date" ? startOfDay(date) : date;
  const minComparable = minDate ? (mode === "date" ? startOfDay(minDate) : minDate) : undefined;
  const maxComparable = maxDate ? (mode === "date" ? startOfDay(maxDate) : maxDate) : undefined;
  if (minComparable && isBefore(left, minComparable)) return { valid: false, date: null, reason: 'minDate' };
  if (maxComparable && isAfter(left, maxComparable)) return { valid: false, date: null, reason: 'maxDate' };
  return { valid: true, date };
};

const parseInputValue = (value: string, mode: DateFieldMode): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const digitsCount = trimmed.replace(/\D/g, "").length;
  const minDigits = mode === "datetime" ? 12 : 8;

  if (digitsCount < minDigits) {
    return null;
  }

  // Strict validation: extract components and validate before parsing
  // Pattern for dd.MM.yyyy or d.M.yyyy
  const datePattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/;
  const dateMatch = trimmed.match(datePattern);

  if (dateMatch) {
    const day = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);
    const year = parseInt(dateMatch[3], 10);

    // Strict validation of date components
    if (day < 1 || day > 31) return null;
    if (month < 1 || month > 12) return null;
    if (year < 1900 || year > 2100) return null;

    // For datetime mode, also validate time components
    if (mode === "datetime") {
      const timePattern = /(\d{1,2}):(\d{2})/;
      const timeMatch = trimmed.match(timePattern);

      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);

        if (hours < 0 || hours > 23) return null;
        if (minutes < 0 || minutes > 59) return null;
      }
    }
  }

  const formats =
    mode === "datetime"
      ? [...DATE_TIME_INPUT_FORMATS, ...DATE_INPUT_FORMATS]
      : DATE_INPUT_FORMATS;

  for (const fmt of formats) {
    try {
      const parsed = parse(trimmed, fmt, new Date());
      if (isValid(parsed)) {
        if (mode === "datetime" && fmt.includes("H")) {
          const inputHasTime = /\d{1,2}:\d{2}/.test(trimmed);
          if (!inputHasTime) continue;
        }

        const year = parsed.getFullYear();
        if (year < 1900 || year > 2100) {
          continue;
        }

        // Double-check that parsed date components match input
        // This prevents date-fns from "correcting" invalid dates like day 43
        if (dateMatch) {
          const inputDay = parseInt(dateMatch[1], 10);
          const inputMonth = parseInt(dateMatch[2], 10);
          const inputYear = parseInt(dateMatch[3], 10);

          if (parsed.getDate() !== inputDay ||
            parsed.getMonth() + 1 !== inputMonth ||
            parsed.getFullYear() !== inputYear) {
            continue; // Skip this format if components don't match
          }
        }

        return parsed;
      }
    } catch {
      // ignore parse errors
    }
  }

  try {
    const parsedISO = parseISO(trimmed);
    if (isValid(parsedISO)) {
      const year = parsedISO.getFullYear();
      if (year >= 1900 && year <= 2100) {
        return parsedISO;
      }
    }
  } catch {
    // ignore ISO parse errors
  }

  return null;
};

export function DateField(props: DateFieldProps) {
  // Only remount when mode changes, not when value changes
  // This prevents the popover from closing when selecting a date in datetime mode
  const key = `datefield-${props.mode}`;
  return <DateFieldInternal key={key} {...props} />;
}

function DateFieldInternal({
  value,
  onChange,
  mode = "date",
  placeholder,
  className,
  minDate,
  maxDate,
  disabled = false,
  allowClear = true,
  minutesStep: _minutesStep = 15,
  calendarStartDay = 1,
  label,
  required,
  name,
  id,
  helperText,
  openToDate,
  ariaInvalid,
  ariaDescribedBy,
}: DateFieldProps) {
  const { t, language } = useLanguage();
  const locale = language === 'en' ? enUS : uk;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatValue(value, mode));
  const [inputError, setInputError] = useState<'format' | 'minDate' | 'maxDate' | null>(null);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState({
    hours: value ? format(value, "HH") : "00",
    minutes: value ? format(value, "mm") : "00",
  });
  const [_popoverPlacement, setPopoverPlacement] = useState<"bottom" | "top">("bottom");
  const [_popoverAlign, setPopoverAlign] = useState<"left" | "right">("left");
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [popoverReady, setPopoverReady] = useState(false);
  const prevIsPopoverOpenRef = useRef(isPopoverOpen);
  const prevValueRef = useRef(value);

  // Sync internal state when value prop changes externally
  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = value;

    // Check if value actually changed (compare timestamps for dates, or null equality)
    const hasChanged = (prevValue instanceof Date && currentValue instanceof Date)
      ? prevValue.getTime() !== currentValue.getTime()
      : prevValue !== currentValue;

    if (hasChanged) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Necessary for syncing external value prop to internal input state
      setInputValue(formatValue(currentValue, mode));
      setInputError(null);
      if (currentValue && mode === "datetime") {
        setTimeValue({
          hours: format(currentValue, "HH"),
          minutes: format(currentValue, "mm"),
        });
      }
      prevValueRef.current = currentValue;
    }
  }, [value, mode]);

  useLayoutEffect(() => {
    if (!isPopoverOpen) return;

    const adjustPlacement = () => {
      if (!containerRef.current || !popoverRef.current) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (containerRect.bottom < 0 || containerRect.top > viewportHeight) {
        setIsPopoverOpen(false);
        return;
      }

      const popoverRect = popoverRef.current.getBoundingClientRect();
      const spaceBelow = viewportHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;
      const margin = 8; // matches mt-2 / mb-2 spacing

      // Determine vertical placement
      let nextPlacement: "bottom" | "top";

      if (spaceBelow >= popoverRect.height + margin) {
        nextPlacement = "bottom";
      } else if (spaceAbove >= popoverRect.height + margin) {
        nextPlacement = "top";
      } else {
        nextPlacement = spaceAbove > spaceBelow ? "top" : "bottom";
      }

      // Determine horizontal alignment
      // Default to right-aligned (near the icon on the right side)
      // Check if right-aligned popover would fit (not extend beyond left viewport edge)
      let nextAlign: "left" | "right";

      const rightAlignedLeft = containerRect.right - popoverRect.width;

      if (rightAlignedLeft >= margin) {
        // Enough space for right-align
        nextAlign = "right";
      } else {
        // Not enough space on left, use left-align
        nextAlign = "left";
      }

      // Calculate fixed position for portal rendering
      let top: number;
      let left: number;

      if (nextPlacement === "bottom") {
        top = containerRect.bottom + margin;
      } else {
        top = containerRect.top - popoverRect.height - margin;
      }

      if (nextAlign === "right") {
        left = Math.max(margin, Math.min(containerRect.right - popoverRect.width, viewportWidth - popoverRect.width - margin));
      } else {
        left = Math.max(margin, Math.min(containerRect.left, viewportWidth - popoverRect.width - margin));
      }

      setPopoverPosition({ top, left });
      setPopoverPlacement((current) => (current === nextPlacement ? current : nextPlacement));
      setPopoverAlign((current) => (current === nextAlign ? current : nextAlign));
      setPopoverReady(true);
    };

    // Position synchronously before browser paint so the popover is interactive
    // on the very first frame – no dead window where clicks fall through.
    adjustPlacement();
    window.addEventListener("resize", adjustPlacement);
    window.addEventListener("scroll", adjustPlacement, true);

    return () => {
      window.removeEventListener("resize", adjustPlacement);
      window.removeEventListener("scroll", adjustPlacement, true);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    // Only reset popoverReady when transitioning from open to closed
    if (!isPopoverOpen && prevIsPopoverOpenRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPopoverReady(false);
      // Discard any uncommitted date pick – next open should show value, not the rejected pendingDate
      setPendingDate(null);
      setInputError((prev) => (prev === 'minDate' ? null : prev));
    }
    prevIsPopoverOpenRef.current = isPopoverOpen;
  }, [isPopoverOpen]);

  // Close popover when clicking outside
  useEffect(() => {
    if (!isPopoverOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPopoverOpen]);

  const handleInputChange = useCallback(
    (text: string) => {
      // Get cursor position in the new unformatted text (before we format it)
      const cursorPos = inputRef.current?.selectionStart ?? text.length;

      // Count digits before cursor in the NEW unformatted text
      const digitsBeforeCursor = text.slice(0, cursorPos).replace(/\D/g, "").length;

      // Apply formatting
      const sanitized = sanitizeUserInput(text, mode);
      setInputValue(sanitized);
      setInputError(null);

      // Calculate cursor position in formatted string
      if (inputRef.current && sanitized) {
        let newPos = 0;
        let digitCount = 0;

        // Find position in formatted string where we have the same number of digits before cursor
        for (let i = 0; i < sanitized.length; i++) {
          if (/\d/.test(sanitized[i])) {
            digitCount++;
            // Place cursor AFTER this digit if we've reached the target count
            if (digitCount === digitsBeforeCursor) {
              newPos = i + 1;
              break;
            }
          }
        }

        // If no digits or fewer digits than expected, place at end
        if (digitCount < digitsBeforeCursor || digitCount === 0) {
          newPos = sanitized.length;
        }

        // Restore cursor position after React updates the input
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newPos, newPos);
          }
        });
      }

      // If empty, clear the date
      if (!sanitized.trim()) {
        onChange(null);
        return;
      }

      // Try to parse if input looks complete
      const isComplete = hasCompleteInput(sanitized, mode);
      if (isComplete) {
        const parsed = parseInputValue(sanitized, mode);
        if (parsed) {
          const { valid, date } = ensureWithinBounds(parsed, minDate, maxDate, mode);
          if (valid && date) {
            onChange(date);
            if (mode === "datetime") {
              setTimeValue({
                hours: format(date, "HH"),
                minutes: format(date, "mm"),
              });
            }
            return;
          }
        }
      }

      // Don't clear the date onChange while editing
      // Keep the existing date until user enters a complete valid date
    },
    [mode, minDate, maxDate, onChange]
  );

  const handleInputBlur = useCallback((e?: React.FocusEvent<HTMLInputElement>) => {
    // If focus is moving into the popover (user clicking a day or time field),
    // skip the blur-driven onChange. Otherwise the blur's onChange races with
    // the day-click and re-renders can detach the click target before mouseup,
    // eating the first click on a date.
    const nextTarget = e?.relatedTarget as Node | null;
    if (nextTarget && popoverRef.current?.contains(nextTarget)) {
      return;
    }

    const text = inputValue.trim();

    if (!text) {
      setInputError(null);
      onChange(null);
      return;
    }

    // Try to parse the input
    const parsed = parseInputValue(text, mode);
    if (parsed) {
      const result = ensureWithinBounds(parsed, minDate, maxDate, mode);
      if (result.valid && result.date) {
        // Format the date nicely on blur
        setInputValue(formatValue(result.date, mode));
        setInputError(null);
        onChange(result.date);
        if (mode === "datetime") {
          setTimeValue({
            hours: format(result.date, "HH"),
            minutes: format(result.date, "mm"),
          });
        }
        return;
      } else if (!result.valid) {
        // Date is out of bounds
        if (value) {
          setInputValue(formatValue(value, mode));
          setInputError(null);
        } else {
          setInputError(result.reason);
        }
        return;
      }
    }

    // If we have a valid date already, restore the formatted value
    if (value) {
      setInputValue(formatValue(value, mode));
      setInputError(null);
    } else {
      // Invalid input and no existing value - format error
      setInputError('format');
    }
  }, [inputValue, mode, minDate, maxDate, onChange, value]);

  const clearValue = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (disabled) return;
    setInputValue("");
    setInputError(null);
    onChange(null);
  };

  const handleDateSelect = useCallback((date: Date | undefined) => {
    if (!date) {
      onChange(null);
      setInputValue("");
      setInputError(null);
      setPendingDate(null);
      if (mode === "date") {
        setIsPopoverOpen(false);
      }
      return;
    }

    const finalDate = new Date(date);

    // For datetime mode, preserve the selected time
    if (mode === "datetime") {
      finalDate.setHours(parseInt(timeValue.hours) || 0);
      finalDate.setMinutes(parseInt(timeValue.minutes) || 0);
      finalDate.setSeconds(0);
      finalDate.setMilliseconds(0);
    } else {
      // For date mode, set time to start of day
      finalDate.setHours(0, 0, 0, 0);
    }

    const result = ensureWithinBounds(finalDate, minDate, maxDate, mode);

    if (result.valid && result.date) {
      onChange(result.date);
      setInputValue(formatValue(result.date, mode));
      setInputError(null);
      setPendingDate(null);
      if (mode === "date") {
        setIsPopoverOpen(false);
      }
    } else if (!result.valid && mode === "datetime" && result.reason === 'minDate') {
      // Hold the user's date pick locally so they can adjust the time without
      // losing their selection. Don't fire onChange until the combined value
      // satisfies minDate.
      setPendingDate(finalDate);
      setInputError('minDate');
    } else if (!result.valid) {
      setInputError(result.reason);
    }
  }, [mode, minDate, maxDate, onChange, timeValue]);

  const applyTime = useCallback((hoursNum: number, minutesNum: number) => {
    const hours = hoursNum.toString().padStart(2, "0");
    const minutes = minutesNum.toString().padStart(2, "0");
    setTimeValue({ hours, minutes });

    const baseDate = pendingDate ?? value;
    if (baseDate) {
      const newDate = new Date(baseDate);
      newDate.setHours(hoursNum);
      newDate.setMinutes(minutesNum);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);

      const result = ensureWithinBounds(newDate, minDate, maxDate, mode);
      if (result.valid && result.date) {
        onChange(result.date);
        setInputValue(formatValue(result.date, mode));
        setInputError(null);
        setPendingDate(null);
      } else {
        // Still invalid: keep pendingDate aligned with the new time so the
        // calendar selection stays visible while the user continues editing.
        setPendingDate(newDate);
        if (!result.valid) setInputError(result.reason);
      }
    }
  }, [pendingDate, value, mode, minDate, maxDate, onChange]);

  const timeDisplayValue = `${(timeValue.hours || "00").padStart(2, "0")}:${(timeValue.minutes || "00").padStart(2, "0")}`;

  // Keydown-driven overwrite editing: each digit replaces the character at the
  // caret, so typing at position 0 swaps the hours without pushing digits to
  // the right. The colon at index 2 is skipped, never typed.
  const handleTimeInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const pos = input.selectionStart ?? 0;

    // Arrow up/down – cycle hours or minutes based on caret segment.
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const onHours = pos <= 2;
      const delta = e.key === "ArrowUp" ? 1 : -1;
      const h = parseInt(timeValue.hours) || 0;
      const m = parseInt(timeValue.minutes) || 0;
      const newH = onHours ? (h + delta + 24) % 24 : h;
      const newM = onHours ? m : (m + delta + 60) % 60;
      applyTime(newH, newM);
      requestAnimationFrame(() => {
        if (onHours) input.setSelectionRange(0, 2);
        else input.setSelectionRange(3, 5);
      });
      return;
    }

    // Digit – overwrite at caret position, skipping the colon.
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      let writePos = pos;
      if (writePos === 2) writePos = 3; // hop over colon
      if (writePos >= 5) return; // caret past the end

      const chars = timeDisplayValue.split("");
      chars[writePos] = e.key;
      const next = chars.join("");
      const [hStr, mStr] = next.split(":");
      const h = Math.min(23, Math.max(0, parseInt(hStr) || 0));
      const m = Math.min(59, Math.max(0, parseInt(mStr) || 0));
      applyTime(h, m);

      let nextPos = writePos + 1;
      if (nextPos === 2) nextPos = 3; // hop over colon on the way out
      requestAnimationFrame(() => {
        input.setSelectionRange(nextPos, nextPos);
      });
      return;
    }

    // Backspace – overwrite previous digit with "0", move caret back.
    if (e.key === "Backspace") {
      e.preventDefault();
      let targetPos = pos - 1;
      if (targetPos === 2) targetPos = 1; // hop back over colon
      if (targetPos < 0) return;

      const chars = timeDisplayValue.split("");
      chars[targetPos] = "0";
      const next = chars.join("");
      const [hStr, mStr] = next.split(":");
      applyTime(parseInt(hStr) || 0, parseInt(mStr) || 0);
      requestAnimationFrame(() => {
        input.setSelectionRange(targetPos, targetPos);
      });
      return;
    }

    // Delete – overwrite the digit under the caret with "0".
    if (e.key === "Delete") {
      e.preventDefault();
      let targetPos = pos;
      if (targetPos === 2) targetPos = 3;
      if (targetPos >= 5) return;

      const chars = timeDisplayValue.split("");
      chars[targetPos] = "0";
      const next = chars.join("");
      const [hStr, mStr] = next.split(":");
      applyTime(parseInt(hStr) || 0, parseInt(mStr) || 0);
      return;
    }

    // Allow caret navigation and focus shifts – block every other printable key.
    if (["ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) return;
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  }, [timeValue, applyTime, timeDisplayValue]);

  const renderPopover = () => {
    if (!isPopoverOpen || typeof document === 'undefined') return null;

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const popoverWidth =
      mode === "datetime" && isMobile ? "min(95vw, 340px)" : undefined;

    const popoverContent = (
      <div
        ref={popoverRef}
        className="fixed z-[9999] inline-block rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-200 overflow-y-auto"
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
          maxWidth: '95vw',
          maxHeight: 'calc(100vh - 32px)',
          visibility: popoverReady ? "visible" : "hidden",
          pointerEvents: popoverReady ? "auto" : "none",
          ...(popoverWidth ? { width: popoverWidth } : {}),
        }}
      >
        <Calendar
          mode="single"
          selected={pendingDate ?? value ?? undefined}
          onSelect={handleDateSelect}
          locale={locale}
          weekStartsOn={calendarStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6}
          disabled={(date) => {
            if (minDate && isBefore(date, startOfDay(minDate))) return true;
            if (maxDate && isAfter(date, startOfDay(maxDate))) return true;
            return false;
          }}
          defaultMonth={openToDate || value || undefined}
          startMonth={minDate || new Date(1900, 0)}
          endMonth={maxDate || new Date(new Date().getFullYear() + 10, 11)}
          className="p-2 [--cell-size:24px]"
          classNames={{
            month: "flex flex-col w-full gap-2",
            week: "flex w-full mt-1",
          }}
        />

        {mode === "datetime" && (
          <div className="border-t border-gray-200 px-2 py-2">
            <p className="text-xs font-semibold text-gray-700 mb-1.5">{t('common.datePicker.time')}</p>
            <input
              type="text"
              inputMode="numeric"
              value={timeDisplayValue}
              onKeyDown={handleTimeInputKeyDown}
              onChange={() => {
                // Controlled via onKeyDown – this no-op keeps React from
                // warning about a value-without-onChange input.
              }}
              maxLength={5}
              className={cn(
                "w-20 h-8 text-center rounded-md border text-sm font-medium focus-visible:ring-2 outline-none",
                pendingDate && inputError === 'minDate'
                  ? "border-red-400 focus-visible:border-red-500 focus-visible:ring-red-500/20"
                  : "border-gray-300 focus-visible:border-blue-500 focus-visible:ring-blue-500/20"
              )}
              placeholder={`${t('common.datePicker.hourPlaceholder')}:${t('common.datePicker.minutePlaceholder')}`}
              aria-label={t('common.datePicker.time')}
            />
            {pendingDate && inputError === 'minDate' && minDate && (
              <p className="mt-1.5 text-xs text-red-600">
                {t('common.datePicker.warningTimeAfter')} {format(minDate, DATE_TIME_FORMAT)}
              </p>
            )}
          </div>
        )}
      </div>
    );

    return createPortal(popoverContent, document.body);
  };

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {(label || helperText) && (
        <div className="flex items-center justify-between">
          {label && (
            <label htmlFor={id} className="text-sm font-medium text-gray-700">
              {label}
              {required ? <span className="text-red-500">*</span> : null}
            </label>
          )}
          {helperText && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className="relative"
      >
        <div
          className={cn(
            "flex items-center gap-1.5 rounded-lg border px-3 h-10 transition focus-within:ring-2",
            disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
              : "border-gray-300 bg-white text-gray-800 hover:border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-500/20",
            inputError && "!border-red-400 !ring-red-300/40"
          )}
          onClick={(event) => {
            if ((event.target as HTMLElement).tagName !== "INPUT" && !disabled) {
              setIsPopoverOpen(true);
            }
          }}
        >
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="text"
            value={inputValue}
            placeholder={placeholder ?? (mode === "datetime" ? t('common.datePicker.dateTimePlaceholder') : t('common.datePicker.datePlaceholder'))}
            disabled={disabled}
            onChange={(event) => handleInputChange(event.target.value)}
            onBlur={handleInputBlur}
            onFocus={() => !disabled && setIsPopoverOpen(true)}
            aria-label={label || placeholder || t('common.datePicker.selectDate')}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedBy}
            className="h-9 flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-gray-400"
          />
          <div className="flex items-center gap-1 flex-shrink-0">
            {allowClear && !disabled && inputValue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearValue}
                aria-label={t('common.datePicker.clearDate')}
                className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                if (!disabled) setIsPopoverOpen(true);
              }}
              aria-label={t('common.datePicker.openCalendar')}
              aria-expanded={isPopoverOpen}
              aria-haspopup="dialog"
              className={cn(
                "h-8 w-8 p-0 flex-shrink-0",
                disabled
                  ? "cursor-not-allowed bg-gray-100 text-gray-400"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-blue-600"
              )}
              disabled={disabled}
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {renderPopover()}
      </div>
      {inputError && (
        <p className="text-xs text-red-500">
          {inputError === 'minDate'
            ? t('common.datePicker.errorMinDate')
            : inputError === 'maxDate'
              ? t('common.datePicker.errorMaxDate')
              : `${t('common.datePicker.errorFormat')} ${mode === "datetime" ? t('common.datePicker.exampleDateTime') : t('common.datePicker.exampleDate')}`
          }
        </p>
      )}
    </div>
  );
}
